import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Button } from '@/components/base/buttons'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useCreateLeadPayment, useSaveBillingDetails } from '@/hooks/api/useLeadOnboarding'
import { ArrowRightIcon, Edit05Icon, PlusIcon, Building07Icon, Mail01Icon, MarkerPin03Icon } from '@untitledui-icons/react/line'
import { parseGoogleAddress } from '@/lib/addressParser'
import { EditCustomerModal, type CustomerFormData } from '../components'
import { COUNTRIES, vatRequired } from '@/lib/countries'

// Initialize Stripe
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
)

interface PaymentStepProps {
  onNext: () => void
  onBack: () => void
}


// The actual payment form that uses Stripe hooks
function PaymentForm(props: PaymentStepProps) {
  const { onNext } = props
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  
  const {
    email,
    placeDetails,
    billingCountry,
    paymentToken,
  } = useOnboardingStore()
  
  const saveBillingDetails = useSaveBillingDetails()

  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isSubmittingRef = useRef(false) // Prevent multiple submissions
  
  // Terms acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  // Parse address components from Google Places address string if not already parsed
  const parsedAddress = (() => {
    if (placeDetails?.postcode && placeDetails?.city) {
      return {
        street: placeDetails.addressLine1 || placeDetails.address?.split(',')[0]?.trim() || '',
        city: placeDetails.city,
        postcode: placeDetails.postcode,
      }
    }
    return parseGoogleAddress(placeDetails?.address || '')
  })()

  // Customer / billing info — edited via modal
  const [customerData, setCustomerData] = useState<CustomerFormData>({
    companyName: placeDetails?.name || '',
    billingEmail: email || '',
    billingAddress: parsedAddress.street,
    billingCity: parsedAddress.city,
    billingPostalCode: parsedAddress.postcode,
    billingCountry: billingCountry || 'NL',
  })
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false)

  // VAT number — required for international billing, otherwise hidden until user adds it
  const [vatNumber, setVatNumber] = useState('')
  const [showVat, setShowVat] = useState(false)
  const isVatRequired = vatRequired(customerData.billingCountry)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent multiple submissions
    if (!stripe || !elements || isSubmittingRef.current || isProcessing) {
      return
    }

    if (isVatRequired && !vatNumber.trim()) {
      setShowVat(true)
      setError(t('resources.signup.payment.error_vat_required', 'A VAT / Tax ID number is required for your country'))
      return
    }

    isSubmittingRef.current = true
    setIsProcessing(true)
    setError(null)

    try {
      // Build billing data
      const billingData: Record<string, unknown> = {
        billing_email: customerData.billingEmail || undefined,
        company_name: customerData.companyName || undefined,
        billing_address: customerData.billingAddress || undefined,
        billing_city: customerData.billingCity || undefined,
        billing_postal_code: customerData.billingPostalCode || undefined,
        billing_country: customerData.billingCountry || undefined,
        vat_number: vatNumber.trim() || undefined,
      }
      
      await saveBillingDetails(billingData)

      // Use confirmPayment - Stripe handles billing details via PaymentElement
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Redirect methods (iDEAL) leave the page — the payment-link token must
          // survive the round-trip since no other payment-link state does
          return_url: `${window.location.origin}/signup/confirmation${paymentToken ? `?token=${encodeURIComponent(paymentToken)}` : ''}`,
          payment_method_data: {
            billing_details: {
              name: customerData.companyName || undefined,
              email: customerData.billingEmail || undefined,
              address: {
                line1: customerData.billingAddress || undefined,
                city: customerData.billingCity || undefined,
                postal_code: customerData.billingPostalCode || undefined,
                country: customerData.billingCountry || undefined,
              },
            },
          },
        },
        redirect: 'if_required', // Only redirect for payment methods that require it (iDEAL, etc.)
      })

      if (stripeError) {
        setError(stripeError.message || t('resources.signup.payment.error_payment_failed'))
      } else if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        onNext()
      } else {
        // Handle other statuses if needed
        onNext()
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      const backendMessage = Array.isArray(detail)
        ? detail.map((e: any) => e.msg).join(', ')
        : typeof detail === 'string' ? detail : err?.message
      setError(backendMessage || t('resources.signup.payment.error_payment_failed'))
    } finally {
      // Reset here, not in catch: a declined/incomplete confirmPayment returns
      // stripeError without throwing, and the latch must release for retries
      isSubmittingRef.current = false
      setIsProcessing(false)
    }
  }

  return (
    <div className="flex w-full h-full flex-1 flex-col lg:flex-row">
      {/* Left side - Form (scrollable) */}
      <div className="flex-1 lg:basis-1/2 flex justify-center overflow-y-auto px-5 pb-24 sm:px-6 lg:justify-end lg:pr-[64px]">
        <div className="w-full max-w-[480px] pt-8 sm:pt-12 lg:pt-[90px]">
          <div className="flex flex-col gap-6">
            {/* Back button */}
            <button
              type="button"
              onClick={props.onBack}
              className="flex cursor-pointer items-center gap-1 text-sm font-medium text-[#535862] hover:text-[#181d27] w-fit"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('resources.signup.common.back', 'Back')}
            </button>

            {/* Header */}
            <div className="flex flex-col gap-2">
              <h1 className="text-[30px] font-semibold leading-[38px] text-black">
                {t('resources.signup.payment.title')}
              </h1>
              <p className="text-base text-black">
                {t('resources.signup.payment.subtitle')}
              </p>
            </div>

            <hr className="border-[#e7eaeb]" />

            {/* Customer / billing details */}
            <div className="flex flex-col gap-3">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold leading-6 text-[#222]">
                  {t('resources.signup.payment.billing_details')}
                </p>
                <button
                  type="button"
                  onClick={() => setIsCustomerModalOpen(true)}
                  className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#368f8b] transition-colors hover:text-[#2a7370]"
                >
                  {t('resources.signup.common.edit', 'Edit')}
                  <Edit05Icon className="h-5 w-5" />
                </button>
              </div>

              {/* Info rows */}
              <div className="flex flex-col gap-4">
                {/* Company name */}
                {customerData.companyName && (
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <Building07Icon className="h-6 w-6 text-[#333]" />
                    </div>
                    <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                      {customerData.companyName}
                    </p>
                  </div>
                )}

                {/* Billing email */}
                {customerData.billingEmail && (
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <Mail01Icon className="h-6 w-6 text-[#333]" />
                    </div>
                    <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                      {customerData.billingEmail}
                    </p>
                  </div>
                )}

                {/* Address */}
                {(customerData.billingAddress || customerData.billingCity) && (
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <MarkerPin03Icon className="h-6 w-6 text-[#333]" />
                    </div>
                    <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                      {[customerData.billingAddress, customerData.billingPostalCode, customerData.billingCity]
                        .filter(Boolean)
                        .join(', ')}
                      {customerData.billingCountry && `, ${COUNTRIES.find(c => c.code === customerData.billingCountry)?.name || customerData.billingCountry}`}
                    </p>
                  </div>
                )}

                {/* VAT number — required internationally, otherwise add button or inline display */}
                {isVatRequired || showVat || vatNumber ? (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-[#666]">
                      {isVatRequired
                        ? t('resources.signup.payment.vat_number_required', 'VAT / Tax ID number (required)')
                        : t('resources.signup.payment.vat_number', 'VAT / Tax ID number')}
                    </label>
                    <input
                      type="text"
                      required={isVatRequired}
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value)}
                      placeholder={t('resources.signup.payment.vat_number_placeholder', 'e.g. NL123456789B01')}
                      autoFocus={showVat && !vatNumber}
                      className="block w-full rounded-lg border border-[#d5d7da] bg-white px-3.5 py-2.5 text-base text-[#181d27] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] placeholder:text-[#717680] focus:border-[#181d27] focus:ring-1 focus:ring-[#181d27] focus:outline-none"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowVat(true)}
                    className="flex items-center gap-1.5 text-base font-medium text-[#368f8b] hover:text-[#2a7370] transition-colors cursor-pointer w-fit"
                  >
                    <PlusIcon className="h-5 w-5" />
                    {t('resources.signup.payment.add_vat', 'Add VAT number')}
                  </button>
                )}
              </div>
            </div>

            {/* Edit customer modal */}
            <EditCustomerModal
              isOpen={isCustomerModalOpen}
              onClose={() => setIsCustomerModalOpen(false)}
              onSave={(data) => setCustomerData(data)}
              data={customerData}
            />

            <hr className="border-[#e7eaeb]" />

            {/* Payment method */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <p className="text-sm font-semibold text-[#333]">
                {t('resources.signup.payment.payment_method')}
              </p>

              {/* Stripe PaymentElement - shows all enabled payment methods */}
              <PaymentElement
                options={{
                  layout: 'tabs',
                  wallets: {
                    applePay: 'never',
                    googlePay: 'never',
                  },
                  defaultValues: {
                    billingDetails: {
                      name: customerData.companyName || '',
                      email: customerData.billingEmail || '',
                      address: {
                        country: customerData.billingCountry || 'NL',
                      },
                    },
                  },
                }}
              />

              {error && (
                <p className="text-sm text-[#f04438]">{error}</p>
              )}

              {/* Terms and conditions checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#d5d7da] text-[#181d27] focus:ring-[#181d27] cursor-pointer"
                />
                <span className="text-sm text-[#535862] leading-5">
                  {t('resources.signup.payment.terms_agreement')}{' '}
                  <a
                    href="https://bonnie.co/terms-of-service/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6941c6] hover:underline font-medium"
                  >
                    {t('resources.signup.payment.terms_of_service')}
                  </a>{' '}
                  {t('resources.signup.payment.and')}{' '}
                  <a
                    href="https://bonnie.co/privacy-statement/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6941c6] hover:underline font-medium"
                  >
                    {t('resources.signup.payment.privacy_policy')}
                  </a>
                </span>
              </label>

              <Button
                type="submit"
                className="w-full !bg-[#0a0d12] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#1a1d22]"
                size="lg"
                isLoading={isProcessing}
                isDisabled={!stripe || !elements || !acceptedTerms}
                iconTrailing={ArrowRightIcon}
              >
                {t('resources.signup.payment.try_bonnie')}
              </Button>
            </form>

            <div className="h-24" />
          </div>
        </div>
      </div>

      {/* Right side - Order summary */}
      <div className="border-t border-[rgba(34,34,34,0.24)] bg-[#fbfbfb] px-5 sm:px-6 lg:flex-1 lg:basis-1/2 lg:border-l lg:border-t-0 lg:pl-[63px] lg:px-0">
        <div className="w-full max-w-[480px] pt-8 pb-8 lg:pt-[90px]">
          <div className="flex flex-col gap-[15px]">
            {/* Product */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <img src="/images/bonnie.png" alt="Bonnie" className="h-[26px] w-[26px]" />
                <span className="text-xl sm:text-2xl font-semibold text-[#222]">{t('resources.signup.payment.product_name')}</span>
              </div>
              <span className="text-lg sm:text-xl font-semibold text-[#222]">{t('resources.signup.payment.free_trial')}</span>
            </div>

            {/* Separator */}
            <div className="h-[10px] flex items-center">
              <div className="w-full border-t border-dashed border-[#e7eaeb]" />
            </div>

            {/* Verification charge */}
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-[#222]">{t('resources.signup.payment.card_verification')}</span>
              <span className="font-semibold text-[#222]">€0.50</span>
            </div>


            {/* Separator */}
            <div className="h-[10px] flex items-center">
              <div className="w-full border-t border-dashed border-[#e7eaeb]" />
            </div>

            {/* Total */}
            <div className="flex items-end justify-between">
              <span className="text-xl font-semibold text-[#222]">{t('resources.signup.payment.total_due')}</span>
              <div className="flex items-end gap-2">
                <span className="text-sm text-[rgba(34,34,34,0.4)] pb-[3px]">EUR</span>
                <span className="text-xl font-semibold text-[#222]">€0.50</span>
              </div>
            </div>

            <p className="text-sm text-[#535862]">
              {t('resources.signup.payment.cancel_anytime', 'Cancel anytime. No questions asked.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Wrapper component that provides Stripe context
export function PaymentStep(props: PaymentStepProps) {
  const { t } = useTranslation()
  const { leadId, stripeClientSecret, setStripeClientSecret } = useOnboardingStore()
  const createPayment = useCreateLeadPayment()
  const [initError, setInitError] = useState<string | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const hasInitializedRef = useRef(false) // Prevent multiple initializations
  
  // Initialize payment intent when component mounts
  useEffect(() => {
    // Skip if already initializing or already tried
    if (isInitializing || hasInitializedRef.current) return
    if (!leadId && !stripeClientSecret) {
      setInitError(t('resources.signup.payment.error_init_payment'))
      return
    }

    // If the secret was pre-loaded (e.g. assistant payment context), skip the API call.
    if (stripeClientSecret) {
      hasInitializedRef.current = true
      return
    }

    hasInitializedRef.current = true
    setIsInitializing(true)
    setInitError(null)

    createPayment.mutateAsync(leadId)
      .then((result) => {
        if (result?.client_secret) {
          setStripeClientSecret(result.client_secret)
        } else {
          throw new Error('No client secret returned')
        }
      })
      .catch((err) => {
        console.error('Failed to create payment intent:', err)
        setInitError(t('resources.signup.payment.error_init_payment'))
        hasInitializedRef.current = false // Allow retry on error
      })
      .finally(() => {
        setIsInitializing(false)
      })
  }, [leadId]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Stripe appearance configuration
  const appearance = {
    theme: 'stripe' as const,
    variables: {
      fontFamily: 'Figtree, system-ui, sans-serif',
      colorPrimary: '#181d27',
      borderRadius: '7px',
    },
  }

  // Show loading state while initializing payment (only use our state, not React Query's)
  if (isInitializing) {
    return (
      <div className="flex w-full h-full flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e7eaeb] border-t-[#181d27]" />
          <p className="text-sm text-[#535862]">{t('resources.signup.payment.loading_payment', 'Setting up payment...')}</p>
        </div>
      </div>
    )
  }

  // Show error if payment init failed
  if (initError || !stripeClientSecret) {
    return (
      <div className="flex w-full h-full flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4 max-w-[400px] text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fef3f2]">
            <svg className="h-6 w-6 text-[#f04438]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-base font-medium text-[#181d27]">
            {leadId
              ? (initError || t('resources.signup.payment.error_init_payment'))
              : t('resources.signup.payment.error_invalid_link', 'This payment link is invalid. Please contact us for a new one.')}
          </p>
          {/* Without a lead there is nothing to retry against */}
          {leadId && (
            <button
              onClick={() => {
                setInitError(null)
                setIsInitializing(true)
                createPayment.mutateAsync(leadId)
                  .then((result) => setStripeClientSecret(result.client_secret))
                  .catch(() => setInitError(t('resources.signup.payment.error_init_payment')))
                  .finally(() => setIsInitializing(false))
              }}
              className="px-4 py-2 text-sm font-medium text-[#6941c6] hover:underline"
            >
              {t('resources.signup.payment.retry', 'Try again')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Only render Elements when we have a valid client secret
  const elementsOptions = {
    clientSecret: stripeClientSecret,
    appearance,
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions} key={stripeClientSecret}>
      <PaymentForm {...props} />
    </Elements>
  )
}
